import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, injectIntl, intlShape } from 'react-intl';

import FieldSet from 'brastrap/common/fieldset/Fieldset';
import FieldInput, {
  filterInputProps,
} from 'brastrap/common/field-input/FieldInput';
import FieldSearch from 'brastrap/common/field-search/FieldSearch';
import FieldSelect from 'brastrap/common/field-select/FieldSelect';

const messages = defineMessages({
  addressee: { id: 'address-field-set.addressee', defaultMessage: 'Addressee' },
  addresseeType: {
    id: 'address-field-set.addresseeType',
    defaultMessage: 'Address to',
  },
  addressToMe: {
    id: 'address-field-set.addresseeType.me',
    defaultMessage: 'Address to me',
  },
  addressToOther: {
    id: 'address-field-set.addresseeType.other',
    defaultMessage: 'Address to someone else',
  },
  search: { id: 'address-field-set.search', defaultMessage: 'Address search' },
  searchTitle: {
    id: 'address-field-set.search-title',
    defaultMessage: 'Search for your address',
  },
  address1: {
    id: 'address-field-set.address1',
    defaultMessage: 'Address Line 1',
  },
  address2: {
    id: 'address-field-set.address2',
    defaultMessage: 'Address Line 2',
  },
  country: { id: 'address-field-set.country', defaultMessage: 'Country' },
  town: { id: 'address-field-set.town-city', defaultMessage: 'Town/City' },
  county: { id: 'address-field-set.county', defaultMessage: 'County' },
  state: { id: 'address-field-set.state', defaultMessage: 'State' },
  province: { id: 'address-field-set.province', defaultMessage: 'Province' },
  postCode: { id: 'address-field-set.postCode', defaultMessage: 'Postcode' },
  postalCode: {
    id: 'address-field-set.postalCode',
    defaultMessage: 'Postal Code',
  },
  yourAddress: {
    id: 'address-field-set.title',
    defaultMessage: 'Your address',
  },
  zipCode: { id: 'address-field-set.zipCode', defaultMessage: 'Zip Code' },
});

// @LOCALE TODO: Address rules should be driven by data from the back end, rather than
// hardcoded in here

const countriesRequiringRegion = {
  81: true, // Canada
  185: true, // USA
};

export const validationRulesCreator = (countryId, includeAddressee = false) => {
  const postCode = ['required', { maxLength: 15 }];
  if (+countryId === 1) {
    postCode.unshift('postcode');
  }
  // @LOCALE TODO: Make general rule for validating states / provinces
  // (this will require a change to validation.js to give the correct error message -
  // it only supports one static error message per rule now).
  // Also these rules should be data driven, not hardcoded
  let region = ['ASCII'];
  if (!countriesRequiringRegion[countryId]) {
    region = [{ maxLength: 30 }, 'ASCII'];
  } else {
    region.push('required');
    if (parseInt(countryId, 10) === 185) {
      region.push('usState');
    } else if (parseInt(countryId, 10) === 81) {
      region.push('caProvince');
    }
  }
  const addressee = includeAddressee
    ? ['required', { maxLength: 60 }, 'ASCII']
    : [];
  return {
    addressee,
    address1: ['required', { maxLength: 30 }, 'ASCII'],
    address2: [{ maxLength: 30 }, 'ASCII'],
    countryId: ['required'],
    postCode,
    town: ['required', { maxLength: 30 }],
    region,
    search: [{ maxLength: 30 }],
  };
};

const regionLabels = {
  1: messages.county,
  81: messages.province,
  185: messages.state,
  default: messages.county,
};
const postCodeLabels = {
  1: messages.postCode,
  81: messages.postalCode,
  185: messages.zipCode,
  default: messages.postCode,
};

const AddressFieldSet = props => {
  const {
    addressee,
    addresseeType,
    address1,
    address2,
    countries,
    countryId,
    id,
    intl: { formatMessage },
    namespace,
    postCode,
    search,
    town,
    region,
    selectedCountryId,
    onChangeAddressee,
  } = props;

  const displayId = Object.keys(id).length > 0;

  // This is to keep the input elements required attribute
  // in sync with redux form's validation rules when
  // the selected country changes
  if (countriesRequiringRegion[selectedCountryId]) {
    region.required = true;
    // If region is not a required field, and required is
    // currently an input prop for the region field we
    // need to delete it
  } else if (region.required) {
    delete region.required;
  }

  return (
    <div>
      <FieldSet>
        {displayId && <input type="hidden" {...filterInputProps(id)} />}
        {addresseeType && (
          <FieldSelect
            {...addresseeType}
            onChange={ev => {
              addresseeType.onChange(ev);
              onChangeAddressee(ev.target.value);
            }}
            label={formatMessage(messages.addresseeType)}
            options={[
              { label: formatMessage(messages.addressToMe), value: 'me' },
              { label: formatMessage(messages.addressToOther), value: 'other' },
            ]}
            modifiers="large inline"
            floatLabel
            required
            note={addresseeType.error}
            id={`${namespace}__addresseeType`}
          />
        )}
        {addressee && (
          <FieldInput
            {...addressee}
            label={formatMessage(messages.addressee)}
            modifiers="large inline"
            floatLabel
            maxLength="60"
            required={addresseeType.value === 'other'}
            note={addressee.error}
            disabled={addresseeType.value !== 'other'}
            id={`${namespace}__addressee`}
          />
        )}
      </FieldSet>
      <FieldSet title={formatMessage(messages.searchTitle)}>
        <FieldSearch
          {...search}
          label={formatMessage(messages.search)}
          modifiers={['large', 'label-nocaps']}
          floatLabel
          id={`${namespace}__search`}
        />
      </FieldSet>
      <FieldSet title={formatMessage(messages.yourAddress)}>
        <FieldInput
          {...address1}
          label={formatMessage(messages.address1)}
          modifiers="large"
          floatLabel
          required
          maxLength="30"
          note={address1.error}
          id={`${namespace}__address1`}
        />
        <FieldInput
          {...address2}
          label={formatMessage(messages.address2)}
          modifiers="large"
          floatLabel
          maxLength="30"
          note={address2.error}
          id={`${namespace}__address2`}
        />
        <FieldInput
          {...town}
          label={formatMessage(messages.town)}
          modifiers="large inline"
          floatLabel
          required
          maxLength="30"
          note={town.error}
          id={`${namespace}__town`}
        />
        <FieldInput
          {...region}
          label={formatMessage(
            regionLabels[selectedCountryId] || regionLabels.default
          )}
          modifiers="large inline"
          floatLabel
          maxLength="30"
          note={region.error}
          id={`${namespace}__region`}
        />
        <FieldInput
          {...postCode}
          label={formatMessage(
            postCodeLabels[selectedCountryId] || postCodeLabels.default
          )}
          modifiers="large inline"
          floatLabel
          required
          maxLength="15"
          note={postCode.error}
          id={`${namespace}__postcode`}
        />
        {!!countries.length && (
          <FieldSelect
            {...countryId}
            label={formatMessage(messages.country)}
            hiddenLabel
            modifiers="large"
            required
            options={countries}
            note={countryId.error}
            id={`${namespace}__country`}
          />
        )}
      </FieldSet>
    </div>
  );
};

//
// The field properties are all objects, because they will be redux-form field objects, rather than string.
//
AddressFieldSet.propTypes = {
  addressee: PropTypes.object,
  addresseeType: PropTypes.object,
  address1: PropTypes.object,
  address2: PropTypes.object,
  countries: PropTypes.array,
  countryId: PropTypes.object,
  id: PropTypes.object,
  intl: intlShape,
  namespace: PropTypes.string,
  onMobile: PropTypes.bool,
  postCode: PropTypes.object,
  search: PropTypes.object,
  town: PropTypes.object,
  region: PropTypes.object,
  selectedCountryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onChangeAddressee: PropTypes.func,
};

AddressFieldSet.defaultProps = {
  address1: {},
  address2: {},
  countries: [],
  countryId: {},
  id: {},
  postCode: {},
  town: {},
  region: {},
};

export default injectIntl(AddressFieldSet);
